import React, { useState, useEffect } from "react";
import propTypes from "prop-types";
import classnames from "classnames";
import { guid, createDataAttributes } from "../../utilities/index";

import "./Tabs.scss";

// https://diegocasmo.github.io/knowledge/blog-posts/software/2017-02-06-a-simple-react-tabs-component.html

/*
  The Tab Container
*/

const getTabs = children => {
  return children.filter(child => {
    // Tab elements can be conditionally included/excluded. This results
    // in the child being 'null'. We do not want to render anything in
    // this scenario.
    if (!child || !child.type) return false;
    return child.type.name === Tab.name;
  });
};

export const Tabs = ({
  id = guid(),
  responsive,
  children,
  activeTab,
  className,
  onChange,
  dataAttrib,
  ...rest
}) => {
  const [isMobile, setIsMobile] = useState(
    document.documentElement.clientWidth < 768
  );

  // update state when screen is resized
  useEffect(() => {
    const handler = () => {
      setIsMobile(document.documentElement.clientWidth < 768);
    };
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("resize", handler);
    };
  }, []);

  const [activeTabIndex, setActiveTabIndex] = useState(activeTab);

  // update state if prop changes
  useEffect(() => {
    setActiveTabIndex(activeTab);
    onChange({ activeTab });
    // disabled to prevent inf loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // receive click event from Tab to update the active tab
  const handleTabClick = (tabIndex, onChildClick) => {
    const theTab = tabIndex === activeTabIndex ? activeTab : tabIndex;
    setActiveTabIndex(theTab);
    onChange({ activeTab: tabIndex });
    if (onChildClick) {
      onChildClick(tabIndex);
    }
  };

  // Encapsulate <Tabs/> component API as props for <Tab/> children
  const renderChildrenWithTabsApiAsProps = which => {
    // ignore any children that isn't a Tab
    const allTabs = getTabs(children);

    // filter tabs for the tab nav
    const tabs =
      // on non-mobile, show all the tabs at top
      !isMobile
        ? allTabs
        : // on mobile, split the nav so the content shows directly under the active tab
        isMobile && which === "beforeActiveTab"
        ? allTabs.filter((child, index) => {
            return child.type.name === Tab.name && index <= activeTabIndex;
          })
        : isMobile && which === "afterActiveTab"
        ? allTabs.filter((child, index) => {
            return child.type.name === Tab.name && index > activeTabIndex;
          })
        : null;

    return React.Children.map(tabs, (child, index) => {
      return React.cloneElement(child, {
        onClick: index => {
          handleTabClick(index, child.props.onClick);
        },
        tabsId: `uikit_tabs_${id}`,
        tabIndex:
          which !== "afterActiveTab"
            ? index
            : allTabs.length - tabs.length + index,
        isActive: index === activeTabIndex && which !== "afterActiveTab"
      });
    });
  };

  const classes = classnames("uikit__tabs", className, {
    uikit__tabs__nav__notResponsive: !responsive,
    uikit__tabs__isMobile: isMobile
  });

  // Render current active tab content
  const renderActiveTabContent = () => {
    // ignore any children that isn't a Tab
    const tabs = getTabs(children);

    // return tab content
    if (tabs?.[activeTabIndex]) {
      return tabs[activeTabIndex].props.children;
    }
  };

  return (
    <div
      role="tablist"
      className={classes}
      {...createDataAttributes(dataAttrib)}
      {...rest}
    >
      <ul role="presentation" className="uikit__tabs__nav">
        {renderChildrenWithTabsApiAsProps("beforeActiveTab")}
      </ul>
      <div
        role="tabpanel"
        id={`uikit_tab_panel_${id}`}
        className="uikit__tabs__active-content"
      >
        {renderActiveTabContent()}
      </div>
      {isMobile && (
        <ul role="presentation" className="uikit__tabs__nav">
          {renderChildrenWithTabsApiAsProps("afterActiveTab")}
        </ul>
      )}
    </div>
  );
};

Tabs.propTypes = {
  id: propTypes.string,
  activeTab: propTypes.number,
  children: propTypes.any,
  className: propTypes.string,
  responsive: propTypes.bool,
  onChange: propTypes.func,
  dataAttrib: propTypes.arrayOf(Object)
};

Tabs.defaultProps = {
  activeTab: 0,
  responsive: true,
  onChange: () => {}
};

/*
  The individual Tab
*/

export const Tab = ({
  tabsId,
  onClick,
  tabIndex,
  isActive,
  title,
  disabled,
  className,
  dataAttrib,
  ...rest
}) => {
  // click event passes index up to Tab so it can switch the active tab
  const handleClick = () => {
    if (!disabled && !isActive && onClick) {
      onClick(tabIndex);
    }
  };

  const classes = classnames(
    "uikit__tabs__nav-item",
    {
      "uikit__tabs__nav-item-disabled": disabled,
      "uikit__tabs__nav-item-active": isActive
    },
    className
  );

  return (
    <li className={classes} {...createDataAttributes(dataAttrib)}>
      <button
        role="tab"
        aria-selected={isActive}
        aria-controls={`uikit_tab_panel_${tabsId}`}
        disabled={disabled}
        type="button"
        onClick={handleClick}
        className="uikit__tabs__nav-item-text"
        {...rest}
      >
        {title}
      </button>
    </li>
  );
};

Tab.propTypes = {
  tabsId: propTypes.string,
  onClick: propTypes.func,
  tabIndex: propTypes.number,
  isActive: propTypes.bool,
  title: propTypes.string.isRequired,
  disabled: propTypes.bool,
  className: propTypes.string,
  dataAttrib: propTypes.arrayOf(Object)
};

Tab.defaultProps = {
  disabled: false
};

export default Tabs;
